import { toAbsoluteUrl } from "./siteUrl";

export type SeoSerpVariables = Record<
  string,
  string | number | undefined | null
>;

export function applySeoTemplate(
  template: string | undefined,
  variables: SeoSerpVariables,
) {
  if (!template) return "";

  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (_match, key: string) => {
    const value = variables[key];

    if (value == null) return "";

    return String(value);
  });
}

export function pathifySeoValue(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function sanitizeSeoPathValue(value: string | undefined | null) {
  if (!value) return "/";
  let output = String(value).trim();

  if (!output) return "/";

  const embeddedUrlIndex = output.search(/https?:\/\//i);

  if (embeddedUrlIndex > 0) {
    output = output.slice(0, embeddedUrlIndex).trim();
  }

  if (/^https?:\/\//i.test(output)) {
    try {
      const parsed = new URL(output);

      output = `${parsed.pathname || "/"}${parsed.search || ""}${parsed.hash || ""}`;
    } catch {
      return "/";
    }
  }

  if (!output.startsWith("/")) {
    output = `/${output}`;
  }

  output = output.replace(/\/{2,}/g, "/");

  return output || "/";
}

export function buildBreadcrumbJsonLd(
  breadcrumbTrailTemplate: string | undefined,
  variables: SeoSerpVariables,
) {
  if (!breadcrumbTrailTemplate) return null;

  const entries = breadcrumbTrailTemplate
    .split("|")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [labelTemplate, pathTemplate = "/"] = entry.split("::");
      const name = applySeoTemplate(labelTemplate, variables).trim();
      const item = sanitizeSeoPathValue(
        applySeoTemplate(pathTemplate, variables).trim() || "/",
      );

      return { name, item };
    })
    .filter((entry) => entry.name);

  if (entries.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: entries.map((entry, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: entry.name,
      item: toAbsoluteUrl(entry.item),
    })),
  };
}
